import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { AppLayout } from "@/components/AppLayout";
import { Target, Plus, Check, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";

const CATEGORIES = ["personal", "fitness", "learning", "financial", "career"];

export default function GoalsPage() {
  const { token } = useAuth();
  const [goals, setGoals] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("personal");
  const [newAction, setNewAction] = useState({});
  const [loading, setLoading] = useState(false);
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => { fetchGoals(); }, []);

  const fetchGoals = async () => {
    try {
      const { data } = await axios.get(`${API}/goals`, { headers });
      setGoals(data.goals);
    } catch (err) { console.error(err); }
  };

  const createGoal = async (e) => {
    e.preventDefault();
    if (!title.trim()) return;
    setLoading(true);
    try {
      await axios.post(`${API}/goals`, { title, description, category }, { headers });
      toast.success("Goal created!");
      setTitle(""); setDescription(""); setShowForm(false);
      fetchGoals();
    } catch (err) { toast.error("Failed"); }
    finally { setLoading(false); }
  };

  const addAction = async (goalId) => {
    const text = newAction[goalId]?.trim();
    if (!text) return;
    try {
      await axios.post(`${API}/goals/${goalId}/actions`, { text }, { headers });
      setNewAction(prev => ({ ...prev, [goalId]: "" }));
      fetchGoals();
    } catch (err) { toast.error("Failed"); }
  };

  const toggleAction = async (goalId, actionId) => {
    try {
      await axios.put(`${API}/goals/${goalId}/actions/${actionId}/toggle`, {}, { headers });
      fetchGoals();
    } catch (err) { toast.error("Failed"); }
  };

  const deleteGoal = async (goalId) => {
    try {
      await axios.delete(`${API}/goals/${goalId}`, { headers });
      fetchGoals();
    } catch (err) { toast.error("Failed"); }
  };

  return (
    <AppLayout>
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-3xl mx-auto px-6 py-8 space-y-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="font-heading text-3xl font-medium mb-2">Goals</h1>
              <p className="text-muted-foreground">Break big dreams into daily micro-actions.</p>
            </div>
            <Button
              data-testid="new-goal-btn"
              onClick={() => setShowForm(!showForm)}
              className="btn-omni bg-primary text-primary-foreground gap-2"
            >
              <Plus className="w-4 h-4" /> New Goal
            </Button>
          </div>

          {/* Create Form */}
          {showForm && (
            <form onSubmit={createGoal} className="card-omni p-8 space-y-5">
              <input
                data-testid="goal-title-input"
                value={title}
                onChange={e => setTitle(e.target.value)}
                className="input-omni w-full border border-input text-lg"
                placeholder="What's your goal?"
                required
              />
              <textarea
                data-testid="goal-desc-input"
                value={description}
                onChange={e => setDescription(e.target.value)}
                className="input-omni w-full border border-input min-h-[60px] py-4 resize-none"
                placeholder="Why does this matter? (optional)"
              />
              <div className="flex flex-wrap gap-2">
                {CATEGORIES.map(c => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setCategory(c)}
                    className={`px-4 py-2 rounded-full text-sm border-2 capitalize transition-all ${
                      category === c ? "border-primary bg-primary/5 text-primary font-semibold" : "border-border/50"
                    }`}
                  >
                    {c}
                  </button>
                ))}
              </div>
              <Button data-testid="create-goal-btn" type="submit" disabled={loading} className="btn-omni bg-primary text-primary-foreground">
                {loading ? "..." : "Create Goal"}
              </Button>
            </form>
          )}

          {/* Goals List */}
          {goals.length === 0 && !showForm && (
            <div className="text-center py-16">
              <Target className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" strokeWidth={1.5} />
              <p className="text-muted-foreground">No goals yet. Start with something small.</p>
            </div>
          )}

          {goals.map(goal => (
            <div key={goal.id} data-testid={`goal-${goal.id}`} className="card-omni p-8">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs px-2 py-0.5 rounded-full bg-primary/10 text-primary capitalize">{goal.category}</span>
                  </div>
                  <h3 className="font-heading text-xl font-medium">{goal.title}</h3>
                  {goal.description && <p className="text-sm text-muted-foreground mt-1">{goal.description}</p>}
                </div>
                <button onClick={() => deleteGoal(goal.id)} className="text-muted-foreground hover:text-destructive transition-colors">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>

              {/* Progress */}
              <div className="mb-4">
                <div className="flex items-center justify-between text-sm mb-1">
                  <span className="text-muted-foreground">Progress</span>
                  <span className="font-semibold">{goal.progress || 0}%</span>
                </div>
                <div className="h-2 bg-muted rounded-full overflow-hidden">
                  <div className="h-full bg-primary rounded-full transition-all duration-500" style={{ width: `${goal.progress || 0}%` }} />
                </div>
              </div>

              {/* Actions */}
              <div className="space-y-2 mb-4">
                {(goal.micro_actions || []).map(action => (
                  <div
                    key={action.id}
                    className="flex items-center gap-3 group cursor-pointer"
                    onClick={() => toggleAction(goal.id, action.id)}
                  >
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${
                      action.completed ? "bg-primary border-primary" : "border-border group-hover:border-primary/50"
                    }`}>
                      {action.completed && <Check className="w-3 h-3 text-primary-foreground" />}
                    </div>
                    <span className={`text-sm ${action.completed ? "line-through text-muted-foreground" : ""}`}>
                      {action.text}
                    </span>
                  </div>
                ))}
              </div>

              {/* Add Action */}
              <div className="flex gap-2">
                <input
                  data-testid={`action-input-${goal.id}`}
                  value={newAction[goal.id] || ""}
                  onChange={e => setNewAction(prev => ({ ...prev, [goal.id]: e.target.value }))}
                  onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); addAction(goal.id); } }}
                  className="input-omni flex-1 border border-input h-10 text-sm"
                  placeholder="Add a micro-action..."
                />
                <Button onClick={() => addAction(goal.id)} variant="ghost" className="rounded-full h-10 w-10 p-0">
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </AppLayout>
  );
}
